import { useIntl, weenatIntl as wintl } from '@weenat/wintl';
import isNil from 'lodash-es/isNil';
import { useEffect, useMemo, useState } from 'react';
import { useClient } from '../ClientProvider';
import { getIrriInitialHistoryState, useHistoryContext } from '../HistoryContext';
import { thresholdColorScale } from '../core/dss/irrigation';
import { mergeMutationState, mergeQueryState } from '../mergeState';
import { thresholdTypes } from '../resources/thresholds.types';
import useMutation from '../useMutation';
import useQuery from '../useQuery';
const useThresholdsLabels = () => {
    const { t } = useIntl();
    const thresholdLabels = useMemo(() => thresholdTypes.reduce((acc, threshold) => ({
        ...acc,
        [threshold]: t(`dss.irritensio.flags.${threshold}`, { capitalize: true })
    }), {}), []);
    return thresholdLabels;
};
/** transforms null thresholds to undefined for forms */
function getInitialThresholdsValues(thresholds) {
    const newThresholds = {
        comfort: !isNil(thresholds.comfort) ? thresholds.comfort : undefined,
        stress: !isNil(thresholds.stress) ? thresholds.stress : undefined,
        vigilance: !isNil(thresholds.vigilance) ? thresholds.vigilance : undefined,
        saturation: !isNil(thresholds.saturation) ? thresholds.saturation : undefined
    };
    return newThresholds;
}
const SORTED_TENSIOMETERS_THRESHOLDS = ['comfort', 'vigilance', 'stress'];
export const useIrritensioThresholds = ({ thresholdsValues }) => {
    const thresholdLabels = useThresholdsLabels();
    return useMemo(() => {
        const thresholds = thresholdTypes
            .reduce((acc, threshold) => acc.concat({
            label: thresholdLabels[threshold],
            value: thresholdsValues[threshold],
            color: thresholdColorScale(threshold),
            type: threshold
        }), [])
            .sort((a, b) => SORTED_TENSIOMETERS_THRESHOLDS.indexOf(b.type) -
            SORTED_TENSIOMETERS_THRESHOLDS.indexOf(a.type));
        const memoFormThresholds = thresholds.filter(({ type }) => type !== 'saturation');
        const valuesFromThresholds = getInitialThresholdsValues(thresholdsValues);
        delete valuesFromThresholds.saturation;
        const initialValues = valuesFromThresholds;
        return {
            formThresholds: memoFormThresholds,
            initialValues
        };
    }, [thresholdsValues]);
};
const SORTED_CAPACITIVE_THRESHOLDS = [
    'saturation',
    'comfort',
    'vigilance',
    'stress'
];
export const useCapacitiveIrrigationThresholds = ({ thresholdsValues }) => {
    const { t } = useIntl();
    const thresholdLabels = useThresholdsLabels();
    thresholdLabels.stress = t('dss.capacitiveIrrigation.dss.stressLabel');
    return useMemo(() => {
        const thresholds = thresholdTypes
            .reduce((acc, threshold) => acc.concat({
            label: thresholdLabels[threshold],
            value: thresholdsValues[threshold],
            color: thresholdColorScale(threshold),
            type: threshold,
            strokeAxis: threshold === 'stress' ? 'black' : undefined
        }), [])
            .sort((a, b) => SORTED_CAPACITIVE_THRESHOLDS.indexOf(a.type) -
            SORTED_CAPACITIVE_THRESHOLDS.indexOf(b.type));
        const memoFormThresholds = thresholds;
        const initialValues = getInitialThresholdsValues(thresholdsValues);
        return {
            formThresholds: memoFormThresholds,
            initialValues
        };
    }, [thresholdsValues]);
};
/** Fetches the default thresholds for a given plot */
export const useGetPlotDefaultThresholds = (plot, shouldFetch = false) => {
    const client = useClient();
    const { soilTexture, availableMeasures } = plot;
    // should work as plots cannot have both HPOT and U_CAPA
    const metric = availableMeasures.includes('HPOT') ? 'HPOT' : 'U_CAPA';
    const defaultThresholdRequest = useQuery(client.thresholds.getDefaultThresholds({ metric }), {
        enabled: shouldFetch && (availableMeasures.includes('HPOT') || availableMeasures.includes('U_CAPA'))
    });
    const allDefaultThresholds = defaultThresholdRequest.data;
    const defaultThresholds = !isNil(soilTexture) && !isNil(allDefaultThresholds)
        ? allDefaultThresholds[soilTexture]
        : { comfort: null, saturation: null, stress: null, vigilance: null };
    return { requestState: defaultThresholdRequest, defaultThresholds };
};
const DEFAULT_THRESHOLDS_PROFILE_ID = -1;
export const DEFAULT_CREATE_THRESHOLDS_PROFILE_ID = -2;
const createDefaultThresholdsProfile = (defaultThresholds) => ({
    id: DEFAULT_THRESHOLDS_PROFILE_ID,
    name: wintl.t('dss.irritensio.dss.default_thresholds_profile', { capitalize: true }),
    thresholds: defaultThresholds,
    linkedPlotNames: [],
    metric: 'HPOT'
});
/**
 * Used to find an existing link between a thresholds profile and a plot
 */
const useGetThresholdsProfileToPlotLink = (plot) => {
    const client = useClient();
    const { id, organizationId } = plot;
    const thresholdsProfilePlotLinksRequest = useQuery(client.thresholds.getThresholdsProfilePlotLinks({ orgId: organizationId, plotId: id }), { enabled: !isNil(id) && !isNil(organizationId) });
    const thresholdsProfilePlotLinks = thresholdsProfilePlotLinksRequest.data ?? [];
    const link = thresholdsProfilePlotLinks.find((l) => l.plotId === id);
    return { requestState: thresholdsProfilePlotLinksRequest, thresholdsProfilePlotLink: link };
};
/**
 * fetches the current thresholds profile id by checking the links between plots and profile
 * will return -1 if there is not existing links between a plot and a profile
 */
export const useGetCurrentThresholdsProfile = (plot, thresholdProfileIdToInitFrom, enabled = isNil(thresholdProfileIdToInitFrom) ? true : false) => {
    const client = useClient();
    const { meta } = plot;
    const thresholdsProfileId = !isNil(meta) && !isNil(meta.thresholdsProfileId) ? meta.thresholdsProfileId : undefined;
    const thresholdsProfileRequest = useQuery(client.thresholds.getThresholdsProfile(thresholdProfileIdToInitFrom ?? thresholdsProfileId), { enabled: !isNil(thresholdsProfileId) || !isNil(thresholdProfileIdToInitFrom) });
    const { defaultThresholds, requestState: defaultThresholdsRequest } = useGetPlotDefaultThresholds(plot, isNil(thresholdsProfileId) && enabled);
    const thresholdsProfile = !isNil(thresholdsProfileRequest.data)
        ? thresholdsProfileRequest.data
        : createDefaultThresholdsProfile(defaultThresholds);
    const mergedRequestsState = mergeQueryState(defaultThresholdsRequest, thresholdsProfileRequest);
    return { requestState: mergedRequestsState, thresholdsProfile };
};
/**
 * Fetches the complete list of thresholds profile available for the plot
 * It adds the default thresholds profile to the list
 */
export const useGetThresholdsProfileList = (plot) => {
    const client = useClient();
    const { availableMeasures, organizationId } = plot;
    // should work as plots cannot have both HPOT and U_CAPA
    const metric = availableMeasures.includes('HPOT') ? 'HPOT' : 'U_CAPA';
    const orgThresholdProfileRequest = useQuery(client.thresholds.getAllThresholdsProfiles({ metric, orgId: organizationId }), { enabled: !isNil(organizationId) });
    const orgThresholdsProfiles = orgThresholdProfileRequest.data ?? [];
    const { defaultThresholds, requestState: defaultThresholdsRequest } = useGetPlotDefaultThresholds(plot, true);
    const data = [
        createDefaultThresholdsProfile(defaultThresholds),
        ...orgThresholdsProfiles
    ];
    const mergedRequestsState = mergeQueryState(defaultThresholdsRequest, orgThresholdProfileRequest);
    return { requestState: mergedRequestsState, data };
};
export const useEditOrCreateThresholdsProfile = ({ plot, onSuccess }) => {
    const client = useClient();
    const { historyState: { thresholdsProfile }, dispatch } = useHistoryContext();
    const [step, setStep] = useState('thresholds');
    const { requestState: defaultThresholdsRequestState } = useGetPlotDefaultThresholds(plot, true);
    /** See {@link https://gitlab.com/weenat-front/client/-/blob/c613a0a9b95390e25db79cb2bc8fc9f96c5c891f/src/hooks/useThresholds.tsx#L424} */
    const keysToInvalidate = [client.plots.get(plot.id).invalidationKey];
    const [linkThresholdsProfileToPlot, linkThresholdsProfileToPlotRequest] = useMutation(client.thresholds.linkThresholdsProfileToPlot(plot.organizationId), { onSuccess, keysToInvalidate });
    const [createThresholdsProfile, createThresholdsProfileRequest] = useMutation(client.thresholds.createThresholdsProfile(plot?.organizationId), {
        onSuccess: async (newThresholdsProfile) => {
            // on creation we just create a new link
            // if there is an existing link it should be erase by the backend and erased with the new one
            await linkThresholdsProfileToPlot({
                plotId: plot.id,
                thresholdsId: newThresholdsProfile.id
            });
        }
    });
    const [editThresholdsProfile, editThresholdsProfileRequest] = useMutation(client.thresholds.updateThresholdsProfile(thresholdsProfile?.id), { onSuccess });
    const handleThresholdChange = (newThreshold, type) => {
        dispatch({
            type: 'updateThresholdProfileThreshold',
            thresholdKey: type,
            thresholdValue: newThreshold
        });
    };
    const handleConfirmThresholds = () => {
        if (thresholdsProfile?.id === DEFAULT_CREATE_THRESHOLDS_PROFILE_ID) {
            setStep('name');
        }
        else {
            setStep('rename');
        }
    };
    const isIrritensio = plot.availableMeasures.includes('HPOT');
    const handleCreateThresholdsProfile = async ({ name }) => {
        const thresholdsFromState = thresholdsProfile?.thresholds;
        if (!isNil(thresholdsFromState)) {
            await createThresholdsProfile({
                name,
                thresholds: thresholdsFromState,
                metric: isIrritensio ? 'HPOT' : 'U_CAPA'
            });
        }
    };
    const handleEditThresholdsProfile = async ({ name }) => {
        const thresholdsFromState = thresholdsProfile?.thresholds;
        if (!isNil(thresholdsFromState)) {
            await editThresholdsProfile({
                name,
                thresholds: thresholdsFromState,
                metric: isIrritensio ? 'HPOT' : 'U_CAPA'
            });
        }
    };
    const state = mergeMutationState(createThresholdsProfileRequest, editThresholdsProfileRequest, linkThresholdsProfileToPlotRequest);
    const isInitialized = !defaultThresholdsRequestState.isLoading;
    return {
        setStep,
        mutationState: state,
        step,
        handleThresholdChange,
        handleConfirmThresholds,
        handleCreateThresholdsProfile,
        handleEditThresholdsProfile,
        isInitialized
    };
};
export const useThresholdContextState = ({ plot, shouldInitThresholdFromPlot = true, thresholdProfileIdToInitFrom }) => {
    const { requestState, thresholdsProfile } = useGetCurrentThresholdsProfile(plot, thresholdProfileIdToInitFrom, shouldInitThresholdFromPlot);
    const [historyState, setHistoryState] = useState(undefined);
    useEffect(() => {
        if (!requestState.isLoading && requestState.isSuccess && isNil(historyState)) {
            setHistoryState(getIrriInitialHistoryState(plot.timezone, plot.availableMeasures.includes('HPOT'), {
                thresholdsDisplayed: true,
                thresholdsProfile,
                highlightThresholds: true
            }));
        }
    }, [thresholdsProfile, historyState]);
    return { historyState, setHistoryState, currentProfileRequestState: requestState };
};
/**
 * Hook to edit the thresholds profile linked to a plot
 */
export const useLinkThresholdsProfileToPlot = ({ plot, thresholdsProfileId, onSuccess, onError }) => {
    const client = useClient();
    // As we're checking the thresholdsProfileId from the plot meta to know the current profile on the plot
    // We need to invalidate the plot when updating the link
    const keysToInvalidate = [client.plots.get(plot.id).invalidationKey];
    const { requestState: getExistingLinkRequestState, thresholdsProfilePlotLink: existingThresholdsProfileToPlotLink } = useGetThresholdsProfileToPlotLink(plot);
    const [linkThresholdsProfileToPlot, linkThresholdsProfileToPlotRequest] = useMutation(client.thresholds.linkThresholdsProfileToPlot(plot.organizationId), { onSuccess, onError, keysToInvalidate });
    const [removeThresholdsProfileFromPlot, removeThresholdsProfileFromPlotRequest] = useMutation(client.thresholds.removeThresholdsProfileFromPlot(existingThresholdsProfileToPlotLink?.id), { onSuccess, onError, keysToInvalidate });
    const handleLinkThresholdsProfileToPlot = () => {
        if (thresholdsProfileId === DEFAULT_THRESHOLDS_PROFILE_ID) {
            if (!isNil(existingThresholdsProfileToPlotLink)) {
                removeThresholdsProfileFromPlot();
            }
            else {
                onSuccess();
            }
        }
        else {
            linkThresholdsProfileToPlot({ plotId: plot.id, thresholdsId: thresholdsProfileId });
        }
    };
    const mutationState = mergeMutationState(linkThresholdsProfileToPlotRequest, removeThresholdsProfileFromPlotRequest);
    const isCheckingForAnExistingLink = getExistingLinkRequestState.isLoading;
    return {
        requestState: mutationState,
        handleLinkThresholdsProfileToPlot,
        isCheckingForAnExistingLink
    };
};
